import type { Branding } from '@sunucupark/shared';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { WebClient } from './client';
import { applyBrandChrome, applyBrandRoles, loadBranding } from './branding';
import { paintAppearance, resolvedScheme } from './appearance';
import { usePreferences } from './state/preferences';

const BrandingContext = createContext<Branding | null>(null);

/** The version of the packaged brand a start without the gateway shows (`main.tsx`). */
export const OFFLINE_VERSION = 'offline';

/** The brand the gateway served at startup. Never fall back to a hard-coded name. */
export function useBranding(): Branding {
  const brand = useContext(BrandingContext);
  if (!brand) throw new Error('useBranding was called outside BrandingProvider');
  return brand;
}

/** The e-mail domain a brand can be looked up by, lower case. */
export function domainOf(email: string | undefined): string | undefined {
  return email?.split('@')[1]?.toLowerCase() || undefined;
}

export interface BrandingProviderProps {
  /** What the gateway answered for this host before the page rendered. */
  initial: Branding;
  client: WebClient;
  children: ReactNode;
}

/**
 * Holds the brand on screen and follows who is signed in (ADR 0009 §2, §7).
 *
 * Before anyone signs in, the address the page was opened at is all the
 * gateway has to go on: a customer with their own webmail host sees their own
 * brand, everyone else sees ours. Signing in adds the one thing that decides
 * it — the account's own domain — so a customer on the shared webmail is
 * repainted into their own name, colour and logo the moment they are in, and
 * back to ours when they sign out.
 */
export function BrandingProvider({ initial, client, children }: BrandingProviderProps) {
  const [brand, setBrand] = useState(initial);
  const colorScheme = usePreferences((state) => state.colorScheme);

  useEffect(() => {
    let current = brand.version;
    const apply = (next: Branding) => {
      if (next.version === current) return;
      current = next.version;
      applyBrandRoles(next, resolvedScheme(colorScheme));
      applyBrandChrome(next);
      // A brand paints the greys as well as the accents, so whatever this
      // person chose for themselves is painted again over it (ADR 0009 §8).
      const preferences = usePreferences.getState();
      void paintAppearance(preferences.appearance, preferences.colorScheme);
      setBrand(next);
    };
    /*
     * The brand follows the account's domain, not the session object: a
     * renewed access token is the same person, and asking again for every
     * renewal only gave the page a chance to repaint itself. The initial brand
     * was asked for the session the page started with (`main.tsx`).
     */
    let domain = domainOf(client.session.snapshot()?.email);
    return client.session.subscribe((session) => {
      const next = domainOf(session?.email);
      // An offline start keeps asking until a real brand has arrived.
      if (next === domain && current !== OFFLINE_VERSION) return;
      domain = next;
      void loadBranding(client.api, window.location.host, next)
        .then(apply)
        // The brand on screen is the last one the gateway agreed to; keeping it
        // is better than a half-painted page because one request failed.
        .catch(() => undefined);
    });
    // The scheme is read when a brand arrives, not depended on: switching light
    // and dark is already repainted by the appearance layer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  return <BrandingContext.Provider value={brand}>{children}</BrandingContext.Provider>;
}
